import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import useStyles from '../../theme/useStyles';
import { formatDate } from '../../utils/format';
import type { RegistrationFormData, RegistrationLookup, RegistrationPerson } from '../../api/endpoints';

// A family's registration as a read-only document: what they see under
// "View / Print", laid out to print (or save as PDF from the print dialog)
// cleanly on A4. Same order as the form. Only filled-in details are listed;
// Aadhaar stays masked and documents appear by name, as the server sends them.

const STATUS_LABEL: Record<string, string> = {
  PENDING: '⏳ Waiting for approval',
  REJECTED: '❌ Not approved',
  APPROVED: '✅ Approved'
};

const formatWhen = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const date = (v?: string) => (v ? formatDate(v) : '');
const aadhaar = (v?: string) => (v && v.length === 12 ? `XXXX XXXX ${v.slice(8)}` : v || '');

interface Props {
  churchName?: string;
  logo?: string;
  /** Church address and phone, for the letterhead. */
  address?: string;
  contactNo?: string;
  result: RegistrationLookup;
  form: RegistrationFormData;
}

const RegistrationSummary = ({ churchName, logo, address, contactNo, result, form }: Props) => {
  const s = useStyles(false);
  const t = s.t;

  const cell: CSSProperties = { padding: '5px 8px', verticalAlign: 'top', fontSize: t.fontSize.md, borderBottom: `1px solid ${t.border}` };
  const labelCell: CSSProperties = { ...cell, color: t.textMuted, width: '38%' };

  /** A two-column table of the rows that have a value. */
  const table = (title: string, rows: Array<[string, ReactNode]>) => {
    const filled = rows.filter(([, v]) => v !== '' && v !== null && v !== undefined);
    if (!filled.length) return null;
    return (
      <div style={{ breakInside: 'avoid', marginBottom: '10px' }}>
        <div style={{ color: t.textAccent, fontWeight: t.fontWeight.bold, fontSize: t.fontSize.md, margin: '8px 0 4px' }}>{title}</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {filled.map(([label, v]) => (
              <tr key={label}><td style={labelCell}>{label}</td><td style={cell}>{v}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const person = (p: RegistrationPerson, title: string, isHead: boolean) => {
    const guest = (p.memberStatus || '').toLowerCase() === 'guest';
    const documents = [
      ['Baptism Certificate', p.baptismCert],
      ['Marriage Certificate', p.marriageCert],
      ['Membership Certificate', p.membershipCert],
      ['ID Proof', p.idProof]
    ].filter(([, name]) => name) as Array<[string, string]>;
    return (
      <div key={p.key || title} style={{
        // Only each section is kept whole: holding a whole person together
        // pushed a long card to the next page and left the first one empty.
        border: `1px solid ${t.border}`, borderRadius: t.radius.lg, padding: '12px 14px', marginBottom: '14px'
      }}>
        <div style={{ fontWeight: t.fontWeight.black, fontSize: t.fontSize.lg, color: t.textMain, marginBottom: '2px', breakAfter: 'avoid' }}>
          {title}
        </div>
        {table('Family', [
          ['Relation to Head', isHead ? 'Head of the family' : p.relationToHead],
          ...(isHead
            ? [['Family Address', form.address], ['Parish', form.parish], ['Family Email', form.email]] as Array<[string, ReactNode]>
            : [])
        ])}
        {table('Personal Details', [
          ['Full Name', p.name],
          ['S/o, D/o, W/o', p.relativeName],
          ['Gender', p.gender],
          ['Date of Birth', date(p.dateOfBirth)],
          ['Marital Status', p.maritalStatus],
          ['Date of Marriage', date(p.dateOfMarriage)],
          ['Blood Group', p.bloodGroup],
          ['Education', p.education],
          ['Occupation', p.occupation]
        ])}
        {table('Contact & Identity', [
          ['Mobile Number', p.mobileNumber],
          ['Aadhaar Number', aadhaar(p.aadhaarNumber)],
          ['Area / Locality', p.location]
        ])}
        {table('Church Membership', [
          ['Member Status', p.memberStatus],
          ['Membership No.', p.barcodeMembershipNo],
          ['Life Status', p.status || 'Alive'],
          ['Date of Death', date(p.dateOfDeath)],
          ['Transferred To', p.transferChurch],
          ...(guest
            ? [['Guest since', date(p.guestFrom)], ['Expected until', date(p.guestUntil)],
               ['Home church', p.guestOrigin], ['Purpose of stay', p.guestPurpose]] as Array<[string, ReactNode]>
            : [])
        ])}
        {table('Sacraments', [
          ['Baptism Date', date(p.baptismDate)],
          ['Confirmation Date', date(p.confirmationDate)],
          ['Communion Status', p.communionStatus]
        ])}
        {table('Documents', documents.map(([label, name]) => [label, `Attached: ${name}`]))}
      </div>
    );
  };

  return (
    <div className="registration-summary">
      {/* Letterhead: the church's logo, name, address and phone — as set in
          Settings › Church Identity — over a rule in the brand colour. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '12px',
        borderBottom: `3px solid ${t.primary}`, marginBottom: '14px'
      }}>
        {logo && (
          <img src={logo} alt="" style={{ width: '72px', height: '72px', objectFit: 'contain', flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0, textAlign: logo ? 'left' : 'center' }}>
          <div style={{ fontSize: '22px', fontWeight: t.fontWeight.black, color: t.primary, lineHeight: 1.2 }}>
            {churchName || 'Church'}
          </div>
          {address && (
            <div style={{ color: t.textMain, fontSize: t.fontSize.md, marginTop: '4px', whiteSpace: 'pre-line' }}>{address}</div>
          )}
          {contactNo && (
            <div style={{ color: t.textMuted, fontSize: t.fontSize.md, marginTop: '2px' }}>📞 {contactNo}</div>
          )}
        </div>
      </div>
      <h1 style={{ margin: '0 0 12px', textAlign: 'center', fontSize: t.fontSize.xxl, color: t.textMain, letterSpacing: '0.5px' }}>
        Family Registration Form
      </h1>

      {table('Registration', [
        ['Reference No.', <strong key="ref">{result.referenceNo}</strong>],
        ['Status', STATUS_LABEL[result.status] || result.status],
        ['Reason', result.status === 'REJECTED' ? result.rejectReason : ''],
        ['Family No.', result.familyId],
        ['Membership No.', result.membershipNo],
        ['Submitted', formatWhen(result.submittedAt)],
        ['Last updated', result.updatedAt !== result.submittedAt ? formatWhen(result.updatedAt) : ''],
        ['Approved', formatWhen(result.approvedAt)],
        ['People in family', String(1 + (form.members?.length || 0))]
      ])}

      {person(form.head, `👤 Family Head · ${form.head.name}`, true)}
      {(form.members || []).map((m, i) => person(m, `👥 Member ${i + 1} · ${m.name}`, false))}

      <div style={{ color: t.textFaint, fontSize: t.fontSize.xs, textAlign: 'center', marginTop: '8px' }}>
        {result.status === 'APPROVED'
          ? 'These are the details the family submitted. The church office may have updated the records since.'
          : 'Details as submitted by the family. Not yet approved by the church office.'}
        {' '}Printed {formatWhen(new Date().toISOString())}.
      </div>
    </div>
  );
};

export default RegistrationSummary;
